import React, { useRef, useState } from "react";
import { Upload, Loader2, X, FileText, Camera } from "lucide-react";
import api, { fileUrl } from "../lib/api";

export default function FileUpload({
  value,
  onChange,
  onUploaded,
  multiple = false,
  accept = "image/*,application/pdf",
  label = "Upload file",
  allowCamera = false,
  testid,
}) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const emit = (val) => {
    if (typeof onChange === "function") onChange(val);
    if (typeof onUploaded === "function") onUploaded(val);
  };

  async function handle(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        const r = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      if (multiple) {
        emit([...(value || []), ...uploaded]);
      } else {
        emit(uploaded[0]);
      }
    } catch (err) {
      setError("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  }

  function remove(idx) {
    if (multiple) {
      const next = (value || []).filter((_, i) => i !== idx);
      emit(next);
    } else {
      emit(null);
    }
  }

  const items = multiple ? (value || []) : value ? [value] : [];

  return (
    <div>
      {/* Hidden inputs (shared by both modes) */}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={handle}
        data-testid={testid ? `${testid}-file-input` : "file-upload-input"}
      />
      {allowCamera && (
        <input
          ref={cameraRef}
          type="file"
          className="hidden"
          accept="image/*"
          capture="environment"
          onChange={handle}
          data-testid={testid ? `${testid}-camera-input` : "file-upload-camera-input"}
        />
      )}

      {allowCamera ? (
        /* Two-button mode: Take Photo + Upload Photo */
        <div className="grid grid-cols-2 gap-3" data-testid={testid ? `${testid}-actions` : "file-upload-actions"}>
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={busy}
            data-testid={testid ? `${testid}-take-photo` : "file-upload-take-photo"}
            className="flex flex-col items-center gap-2 border-2 border-slate-900 bg-ncc-gold p-5 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-60"
          >
            <Camera className="h-6 w-6 text-slate-900" />
            <span className="industrial-label text-slate-900">Take Photo</span>
            <span className="font-mono text-[10px] text-slate-700">Use device camera</span>
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={testid ? `${testid}-upload-photo` : "file-upload-browse"}
            className="flex flex-col items-center gap-2 border-2 border-dashed border-slate-400 bg-slate-50 p-5 transition-all hover:border-slate-900 hover:bg-white"
          >
            <Upload className="h-6 w-6 text-slate-700" />
            <span className="industrial-label text-slate-700">Upload Photo</span>
            <span className="font-mono text-[10px] text-slate-500">Browse · max 10MB</span>
          </button>
          {busy && (
            <div className="col-span-2 flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 bg-white py-2 text-slate-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="industrial-label">Uploading</span>
            </div>
          )}
        </div>
      ) : (
        /* Classic dropzone mode (for documents) */
        <div
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer border-2 border-dashed border-slate-400 bg-slate-50 p-6 text-center transition-colors hover:border-slate-900 hover:bg-white"
          data-testid={testid ? `${testid}-dropzone` : "file-upload-dropzone"}
        >
          {busy ? (
            <div className="flex items-center justify-center gap-2 text-slate-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="industrial-label">Uploading</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-5 w-5 text-slate-600" />
              <span className="industrial-label text-slate-700">{label}</span>
              <span className="font-mono text-xs text-slate-500">Click to browse · max 10MB</span>
            </div>
          )}
        </div>
      )}

      {error && <div className="mt-2 font-mono text-xs text-red-600">{error}</div>}
      {items.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-3">
          {items.map((f, i) => {
            const isImg = (f.content_type || "").startsWith("image/");
            return (
              <div key={i} className="relative border-2 border-slate-900 bg-white p-2">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  data-testid={`file-remove-${i}`}
                  className="absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center bg-slate-900 text-white hover:bg-safety-500 hover:text-slate-900"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                {isImg ? (
                  <img src={fileUrl(f.path)} alt={f.name} className="h-24 w-full object-cover" />
                ) : (
                  <div className="flex h-24 items-center justify-center bg-slate-100">
                    <FileText className="h-8 w-8 text-slate-500" />
                  </div>
                )}
                <div className="mt-2 truncate font-mono text-xs text-slate-700">{f.name}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
